import { HabitSchedule, Timestamp } from '@griply/types';
import { addDays, endOfDay, isBefore, min, startOfDay } from 'date-fns';

import { RRule } from '../lib/rrule';
import { getAlignedDtstart } from './get-aligned-dtstart';
import { getScheduleEndDate } from './get-schedule-end-date';
import { getTimeframeSchedules } from './get-timeframe-schedules';
import {
  memoizeBySchedule,
  memoizeByScheduleAndVariant,
} from './memoize-by-schedule';

export type Options = {
  startDate: Timestamp;
  endDate: Timestamp;
};

// Built without `until` — the caller's `between` end bound clamps instead — so one instance serves every query range.
const buildRule = (schedule: HabitSchedule, dtstart: Timestamp) =>
  new RRule({
    ...RRule.fromString(schedule.rrule.format).origOptions,
    dtstart,
    until: null,
  });

const getScheduleRule = memoizeBySchedule((schedule) =>
  buildRule(schedule, startOfDay(schedule.startDate)),
);

const getAlignedRule = memoizeByScheduleAndVariant<Timestamp, RRule>(
  (alignedDtstart) => `${alignedDtstart.getTime()}`,
  (schedule, alignedDtstart) => buildRule(schedule, alignedDtstart),
);

const getBackfillRule = memoizeByScheduleAndVariant<Timestamp, RRule>(
  (earliestCompletionDate) => `${startOfDay(earliestCompletionDate).getTime()}`,
  (schedule, earliestCompletionDate) =>
    buildRule(schedule, startOfDay(earliestCompletionDate)),
);

export const getOccurrences = (
  schedules: HabitSchedule[],
  { startDate, endDate }: Options,
) => {
  // Expansion needs an rrule; a schedule without one occurs never, and reading `.format` off it would throw.
  const plannableSchedules = schedules.filter(
    (schedule) => !!schedule.rrule?.format,
  );
  const timeframeSchedules = getTimeframeSchedules(plannableSchedules, {
    startDate,
    endDate,
  });

  const occurrences = timeframeSchedules.reduce<Timestamp[]>(
    (acc, schedule) => {
      if (schedule.frequency?.unit) {
        return acc;
      }

      // if there are completions before the startDate, we take the first completion and start from there to calculate the occurrences
      const completionsBeforeStartDate = isBefore(
        startDate,
        startOfDay(schedule.startDate),
      )
        ? schedule.entries
            .filter((entry) =>
              isBefore(entry.date, startOfDay(schedule.startDate)),
            )
            .sort(
              (entryA, entryB) => entryA.date.getTime() - entryB.date.getTime(),
            )
        : [];
      if (completionsBeforeStartDate.length) {
        const earliestCompletion = completionsBeforeStartDate[0];
        const backfillEnd = min([
          endOfDay(addDays(schedule.startDate, -1)),
          endDate,
        ]);
        const rrule = getBackfillRule(schedule, earliestCompletion.date);

        acc.push(...rrule.between(startDate, backfillEnd, true));
      }

      const scheduleEnd = getScheduleEndDate(schedule);
      const ruleEnd =
        scheduleEnd && isBefore(scheduleEnd, endDate)
          ? min([endOfDay(scheduleEnd), endDate])
          : endDate;
      const baseRule = getScheduleRule(schedule);
      const alignedDtstart = getAlignedDtstart(baseRule, startDate);
      const rrule = alignedDtstart
        ? getAlignedRule(schedule, alignedDtstart)
        : baseRule;

      acc.push(...rrule.between(startDate, ruleEnd, true));

      return acc;
    },
    [],
  );

  const uniqueOccurrences = new Set(
    occurrences.map((occurrence) => startOfDay(occurrence).getTime()),
  );

  return Array.from(uniqueOccurrences).map((occurrence) =>
    endOfDay(new Date(occurrence)),
  );
};
